import { Box, Button, IconButton, MenuItem, Select, Typography } from '@mui/material';
import {
  Code,
  Eye,
  Image,
  Link,
  SpeakerHigh,
  XCircle,
} from '@phosphor-icons/react';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/mode-typescript';
import 'ace-builds/src-noconflict/theme-monokai';
import { useState } from 'react';
import AceEditor from 'react-ace';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import {
  IChatbotFlowModel,
  IPublicChatbotModel,
} from '../../../data/models/chatbot';
import { MessageVersion } from '../../../data/models/flow';

export const sanitizeSchema = {
  ...defaultSchema,

  tagNames: [...(defaultSchema.tagNames || []), 'audio', 'video', 'source'],

  attributes: {
    ...defaultSchema.attributes,
    audio: ['controls', 'src'],
    video: ['controls', 'src', 'width', 'height'],
    source: ['src', 'type'],
  },
};

interface CodeFlyoutProps {
  onClose: () => void;
  chatbot: IChatbotFlowModel | IPublicChatbotModel;
  currentVersion: number | null;
  onVersionChange: (version: number | null) => void;
  contents: MessageVersion[];
}

const isImage = (url = '') => /\.(png|jpe?g|gif|webp|avif)(\?.*)?$/i.test(url);

const isAudio = (url = '') => /\.(mp3|wav|ogg|m4a)(\?.*)?$/i.test(url);

const isVideo = (url = '') => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);

const getEditorMode = (lang: string) => {
  const modeMap: Record<string, string> = {
    javascript: 'javascript',
    js: 'javascript',
    typescript: 'typescript',
    ts: 'typescript',
    html: 'html',
    css: 'css',
    python: 'python',
    py: 'python',
  };
  return modeMap[lang] || 'javascript';
};

const CodeBlock = ({ children, className, chatbot }: any) => {
  const match = /language-(\w+)/.exec(className ?? '');
  const isHtml = match?.[1] === 'html';
  const [isPreview, setIsPreview] = useState(() => isHtml ? true : false);

  if (!match) {
    return (
      <code className="bg-gray-200 text-sm px-1 py-0.5 rounded">
        {children}
      </code>
    );
  }

  const language = match?.[1] ?? 'text';
  const codeContent = String(children).replace(/\n$/, '');

  return (
    <Box sx={{ position: 'relative' }}>
      {isHtml && (
        <Button
          size="small"
          onClick={() => setIsPreview(!isPreview)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            backgroundColor: 'rgba(0,0,0,0.1)',
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.2)',
            },
          }}
        >
          {isPreview ? 'Raw' : 'Preview'}
        </Button>
      )}

      {isPreview ? (
        <Box sx={{ width: '100%', minHeight: '400px', height: '100%' }}>
          <iframe
            srcDoc={codeContent}
            title="Code Preview"
            sandbox="allow-scripts allow-same-origin"
            style={{
              minHeight: 'inherit' ,
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          />
        </Box>
      ) : (
        <AceEditor
          mode={getEditorMode(language)}
          theme="monokai"
          name="chatbot-code"
          value={codeContent}
          fontSize={14}
          style={{
            borderRadius: '8px',
            width: '100%',
          }}
          lineHeight={19}
          showGutter={true}
          highlightActiveLine={true}
          readOnly
          setOptions={{
            useWorker: false,
          }}
        />
      )}
    </Box>
  );
};

const getMarkdownComponents = (chatbot: IChatbotFlowModel | IPublicChatbotModel) => ({
  a: ({ href, children, ...props }: any) => {
    const url = String(href ?? '');

    if (isImage(url)) {
      return (
        <img
          src={url}
          alt={Array.isArray(children) ? children[0] : String(children)}
          style={{ width: '100%', borderRadius: '8px' }}
          {...props}
        />
      );
    }

    if (isAudio(url)) {
      return (
        <audio controls style={{ width: '100%' }} {...props}>
          <source src={url} />
          Your browser does not support the audio element.
        </audio>
      );
    }

    if (isVideo(url)) {
      return (
        <video controls style={{ width: '100%' }} {...props}>
          <source src={url} />
          Your browser does not support the video element.
        </video>
      );
    }

    return (
      <a href={url} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },

  img: ({ src, alt, ...props }: any) => (
    <img
      src={String(src)}
      alt={String(alt ?? 'Image')}
      style={{ width: '80px', borderRadius: '8px' }}
      {...props}
    />
  ),

  code: ({ children, className, ...props }: any) => (
    <CodeBlock children={children} className={className} chatbot={chatbot} {...props} />
  ),
});

export function CodeFlyout({
  onClose,
  chatbot,
  currentVersion,
  onVersionChange,
  contents
}: CodeFlyoutProps) { 
  const [previewStates, setPreviewStates] = useState<Record<number, boolean>>(
    {},
  );
  const extractLanguageFromCode = (codeBlock: string): string => {
    const match = codeBlock.match(/^```(\w+)/);
    return match ? match[1] : 'text';
  };

  const cleanCodeBlock = (codeBlock: string): string => {
    return codeBlock
      .replace(/^```\w*\n?/, '')
      .replace(/```$/, '')
      .trim();
  };

  const togglePreview = (index: number) => {
    setPreviewStates(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const selectedVersion =
    currentVersion ??
    (contents.length > 0 ? contents[contents.length - 1]?.version : null);

  const currentFlyout = contents.find((f: MessageVersion) => f.version === selectedVersion);

  if (!currentFlyout || currentFlyout.contents.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
          backgroundColor: chatbot.pageBackground
            ? chatbot.pageBackground
            : '#1e1e1e',
          color: chatbot.pageText ? chatbot.pageText : '#fff',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px',
            borderBottom: `1px solid ${chatbot.responseContainer}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: chatbot.pageText ? chatbot.pageText : '#fff',
              }}
            >
              Fly-Out
            </Typography>

            {contents.length > 0 && (
              <Select
                value={selectedVersion ?? ''}
                onChange={e => onVersionChange(Number(e.target.value))}
                sx={{
                  color: chatbot.pageText ? chatbot.pageText : '#fff',
                  backgroundColor: chatbot.responseContainer || 'transparent',
                  minWidth: '120px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: chatbot.brandColor || '#3650DE',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: chatbot.brandColor || '#3650DE',
                  },
                  '& .MuiSvgIcon-root': {
                    color: chatbot.pageText ? chatbot.pageText : '#fff',
                  },
                }}
              >
                {contents.map(flyout => (
                  <MenuItem key={flyout.version} value={flyout.version}>
                    Version {flyout.version}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              color: chatbot.pageText ? chatbot.pageText : '#fff',
              '&:hover': {
                color: chatbot.brandColor ? chatbot.brandColor : '#3650DE',
              },
            }}
          >
            <XCircle size={20} />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <Typography
            sx={{
              color: chatbot.pageText ? chatbot.pageText : '#fff',
            }}
          >
            Nenhum conteúdo disponível
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        backgroundColor: chatbot.pageBackground
          ? chatbot.pageBackground
          : '#1e1e1e',
        color: chatbot.pageText ? chatbot.pageText : '#fff',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px',
          borderBottom: `1px solid ${chatbot.responseContainer}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: chatbot.pageText ? chatbot.pageText : '#fff',
            }}
          >
            Fly-Out
          </Typography>

          {contents.length > 0 && (
            <Select
              value={selectedVersion ?? ''}
              onChange={e => onVersionChange(Number(e.target.value))}
              sx={{
                color: chatbot.pageText ? chatbot.pageText : '#fff',
                backgroundColor: chatbot.responseContainer || 'transparent',
                minWidth: '120px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: chatbot.brandColor || '#3650DE',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: chatbot.brandColor || '#3650DE',
                },
                '& .MuiSvgIcon-root': {
                  color: chatbot.pageText ? chatbot.pageText : '#fff',
                },
              }}
            >
              {contents.map(flyout => (
                <MenuItem key={flyout.version} value={flyout.version}>
                  V{flyout.version}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: chatbot.pageText ? chatbot.pageText : '#fff',
            '&:hover': {
              color: chatbot.brandColor ? chatbot.brandColor : '#3650DE',
            },
          }}
        >
          <XCircle size={20} />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px',
        }}
      >
        {currentFlyout.contents.map((item: { content: string; type: string }, index: number) => {
          if (item.type === 'code') {
            const cleanedCode = cleanCodeBlock(item.content);
            const detectedLanguage = extractLanguageFromCode(item.content);
            const isHtmlCode = detectedLanguage === 'html';
            const isPreviewMode = previewStates[index] !== false;

            return (
              <Box
                key={`code-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: chatbot.userContainer,
                    padding: '8px',
                    borderRadius: '8px 8px 0px 0px',
                    justifyContent: 'space-between',
                    border: `1px solid ${chatbot.responseContainer}`,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: chatbot.responseContainer,
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                      }}
                    >
                      <Code
                        size={14}
                        color={
                          chatbot.brandColor ? chatbot.brandColor : '#3650DE'
                        }
                      />
                    </Box>

                    <Typography
                      variant="body1"
                      fontSize={14}
                      fontWeight={400}
                      color={chatbot.pageText ? chatbot.pageText : '#000'}
                    >
                      Code Preview - {detectedLanguage.toUpperCase()}
                    </Typography>
                  </Box>

                  {isHtmlCode && (
                    <Box
                      onClick={() => togglePreview(index)}
                      sx={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: chatbot.brandColor
                          ? chatbot.brandColor
                          : '#3650DE',
                        color: '#fff',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      {isPreviewMode ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            borderRadius: '4px',
                          }}
                        >
                          <Code size={14} color="#fff" />
                          <Typography
                            variant="body1"
                            fontSize={14}
                            fontWeight={400}
                            color="#fff"
                          >
                            Code
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            borderRadius: '4px',
                          }}
                        >
                          <Eye size={14} color="#fff" />
                          <Typography
                            variant="body1"
                            fontSize={14}
                            fontWeight={400}
                            color="#fff"
                          >
                            Preview
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    overflow: 'hidden',
                    position: 'relative',
                    height: '100%',
                    border: `1px solid ${chatbot.responseContainer}`,
                    borderTop: 'none',
                    borderRadius: '0px 0px 8px 8px',
                    backgroundColor: chatbot.responseContainer,
                  }}
                >
                  {isHtmlCode && isPreviewMode ? (
                    <iframe
                      srcDoc={cleanedCode}
                      title={`HTML Preview ${index}`}
                      sandbox="allow-scripts allow-same-origin"
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        backgroundColor: '#fff',
                      }}
                    />
                  ) : (
                    <AceEditor
                      mode={getEditorMode(detectedLanguage)}
                      theme="monokai"
                      name={`flyout-code-editor-${index}`}
                      value={cleanedCode}
                      fontSize={13}
                      width="100%"
                      height="100%"
                      showGutter={true}
                      highlightActiveLine={true}
                      readOnly
                      wrapEnabled={true}
                      setOptions={{
                        useWorker: false,
                        showLineNumbers: true,
                        tabSize: 2,
                        wrap: true,
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          }

          if (item.type === 'link') {
            return (
              <Box
                key={`link-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: chatbot.userContainer,
                    padding: '8px',
                    borderRadius: '8px 8px 0px 0px',
                    justifyContent: 'space-between',
                    border: `1px solid ${chatbot.responseContainer}`,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: chatbot.responseContainer,
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                      }}
                    >
                      <Link
                        size={14}
                        color={
                          chatbot.brandColor ? chatbot.brandColor : '#3650DE'
                        }
                      />
                    </Box>

                    <Typography
                      variant="body1"
                      fontSize={14}
                      fontWeight={400}
                      color={chatbot.pageText ? chatbot.pageText : '#000'}
                    >
                      Link
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    overflow: 'hidden',
                    position: 'relative',
                    padding: '16px',
                    height: '100%',
                    border: `1px solid ${chatbot.responseContainer}`,
                    borderTop: 'none',
                    borderRadius: '0px 0px 8px 8px',
                    backgroundColor: chatbot.responseContainer,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      color: chatbot.pageText ? chatbot.pageText : '#000',
                    }}
                  >
                    Click to visit this website
                  </Typography>

                  <Box
                    component="a"
                    href={item.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      color: chatbot.brandColor
                        ? chatbot.brandColor
                        : '#3650DE',
                      textDecoration: 'none',
                      wordBreak: 'break-all',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {item.content}
                  </Box>
                </Box>
              </Box>
            );
          }

          if (item.type === 'audio') {
            return (
              <Box
                key={`audio-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: chatbot.userContainer,
                    padding: '8px',
                    borderRadius: '8px 8px 0px 0px',
                    justifyContent: 'space-between',
                    border: `1px solid ${chatbot.responseContainer}`,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: chatbot.responseContainer,
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                      }}
                    >
                      <SpeakerHigh
                        size={14}
                        color={
                          chatbot.brandColor ? chatbot.brandColor : '#3650DE'
                        }
                      />
                    </Box>

                    <Typography
                      variant="body1"
                      fontSize={14}
                      fontWeight={400}
                      color={chatbot.pageText ? chatbot.pageText : '#000'}
                    >
                      Audio Preview
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    overflow: 'hidden',
                    position: 'relative',
                    height: '100%',
                    padding: '16px',
                    border: `1px solid ${chatbot.responseContainer}`,
                    borderTop: 'none',
                    borderRadius: '0px 0px 8px 8px',
                    backgroundColor: chatbot.responseContainer,
                  }}
                >
                  <audio
                    controls
                    src={item.content}
                    controlsList="nodownload"
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                    }}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </Box>
              </Box>
            );
          }

          if (item.type === 'image') {
            return (
              <Box
                key={`image-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: chatbot.userContainer,
                    padding: '8px',
                    borderRadius: '8px 8px 0px 0px',
                    justifyContent: 'space-between',
                    border: `1px solid ${chatbot.responseContainer}`,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: chatbot.responseContainer,
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                      }}
                    >
                      <Image
                        size={14}
                        color={
                          chatbot.brandColor ? chatbot.brandColor : '#3650DE'
                        }
                      />
                    </Box>

                    <Typography
                      variant="body1"
                      fontSize={14}
                      fontWeight={400}
                      color={chatbot.pageText ? chatbot.pageText : '#000'}
                    >
                      Image Preview
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    overflow: 'hidden',
                    position: 'relative',
                    height: '400px',
                    border: `1px solid ${chatbot.responseContainer}`,
                    borderTop: 'none',
                    borderRadius: '0px 0px 8px 8px',
                    backgroundColor: chatbot.responseContainer,
                  }}
                >
                  <Box
                    component="a"
                    href={item.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      width: '100%',
                    }}
                  >
                    <img
                      src={item.content}
                      alt={`Image ${index + 1}`}
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        borderRadius: '8px',
                        objectFit: 'contain',
                        maxHeight: '500px',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          }

          if (item.type === 'flyout-output') {
            return (
              <Box
                key={`flyout-output-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: chatbot.userContainer,
                    padding: '8px',
                    borderRadius: '8px 8px 0px 0px',
                    justifyContent: 'space-between',
                    border: `1px solid ${chatbot.responseContainer}`,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Typography
                      variant="body1"
                      fontSize={14}
                      fontWeight={400}
                      color={chatbot.pageText ? chatbot.pageText : '#000'}
                    >
                      Output Content 
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    overflow: 'auto',
                    position: 'relative',
                    padding: '16px',
                    minHeight: '200px',
                    border: `1px solid ${chatbot.responseContainer}`,
                    borderTop: 'none',
                    borderRadius: '0px 0px 8px 8px',
                    backgroundColor: chatbot.responseContainer,
                  }}
                >
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw, rehypeSanitize]}
                    remarkPlugins={[remarkGfm]}
                    components={getMarkdownComponents(chatbot)}
                  >
                    {item.content}
                  </ReactMarkdown>
                </Box>
              </Box>
            );
          }

          return null;
        })}
      </Box>
    </Box>
  );
}
